import * as THREE from "three";

/**
 * The Scale Continuum — the app's signature interaction.
 *
 * A generated world is not "a model you inspect" or "a place you stand in".
 * It is one object on a continuous axis between those, and the same gesture
 * moves it along that axis: grip both controllers and pull your hands apart and
 * the world grows out from between them until it surrounds you; push them
 * together and it shrinks back into something you can turn over and set down.
 *
 * There is deliberately no mode switch, because the moment you have to *choose*
 * between "inspect" and "enter", the two stop feeling like the same thing.
 *
 * Anchoring is what sells it. Scaling happens about the midpoint between the
 * controllers, not about the world origin, so the geometry appears to grow out
 * of your hands rather than lurch off to one side.
 */

const MIN_SCALE = 0.01;
const MAX_SCALE = 40;
/** Below this hand separation the ratio gets numerically silly. */
const MIN_SEPARATION = 0.04;

export interface HandPose {
  readonly position: THREE.Vector3;
  readonly gripping: boolean;
}

interface GrabState {
  readonly startSeparation: number;
  readonly startMidpoint: THREE.Vector3;
  readonly startYaw: number;
  readonly startPosition: THREE.Vector3;
  readonly startQuaternion: THREE.Quaternion;
  readonly startScale: number;
}

/** Single-hand grabs translate and yaw, but never scale. */
interface SingleGrabState {
  readonly startHand: THREE.Vector3;
  readonly startPosition: THREE.Vector3;
}

export class ScaleContinuum {
  readonly #target: THREE.Object3D;
  #both: GrabState | null = null;
  #single: SingleGrabState | null = null;
  #lastScale = 1;
  /**
   * The scale at which the world was first framed to sit in the hands.
   * Posture is judged relative to this, because captures arrive at wildly
   * different authored scales — an absolute threshold would call one world
   * "life size" and another "tabletop" at the same apparent size.
   */
  #referenceScale = 1;

  readonly #midpoint = new THREE.Vector3();
  readonly #separation = new THREE.Vector3();
  readonly #offset = new THREE.Vector3();
  readonly #yawQuat = new THREE.Quaternion();
  readonly #up = new THREE.Vector3(0, 1, 0);

  constructor(target: THREE.Object3D) {
    this.#target = target;
  }

  /** Uniform scale currently applied to the world. 1 = as generated. */
  get scale(): number {
    return this.#lastScale;
  }

  get isGrabbing(): boolean {
    return this.#both !== null || this.#single !== null;
  }

  /**
   * Describes where the world sits on the continuum, for the HUD. These are
   * user-facing words, not measurements — the point is that the person always
   * knows which end of the axis they are at.
   */
  get posture(): "in your hands" | "tabletop" | "room scale" | "life size" {
    const relative = this.#lastScale / this.#referenceScale;
    if (relative < 1.6) {
      return "in your hands";
    }
    if (relative < 4) {
      return "tabletop";
    }
    if (relative < 9) {
      return "room scale";
    }
    return "life size";
  }

  /** Relative size against the framed rest pose, for display. */
  get relativeScale(): number {
    return this.#lastScale / this.#referenceScale;
  }

  update(left: HandPose, right: HandPose): void {
    const bothGripping = left.gripping && right.gripping;

    if (bothGripping) {
      this.#single = null;
      this.#updateTwoHanded(left, right);
      return;
    }

    this.#both = null;

    const active = left.gripping ? left : right.gripping ? right : null;
    if (active === null) {
      this.#single = null;
      return;
    }
    this.#updateOneHanded(active);
  }

  #updateTwoHanded(left: HandPose, right: HandPose): void {
    this.#separation.subVectors(right.position, left.position);
    const distance = this.#separation.length();
    if (distance < MIN_SEPARATION) {
      return;
    }
    this.#midpoint
      .addVectors(left.position, right.position)
      .multiplyScalar(0.5);
    const yaw = Math.atan2(this.#separation.x, this.#separation.z);

    if (this.#both === null) {
      this.#both = {
        startSeparation: distance,
        startMidpoint: this.#midpoint.clone(),
        startYaw: yaw,
        startPosition: this.#target.position.clone(),
        startQuaternion: this.#target.quaternion.clone(),
        startScale: this.#target.scale.x
      };
      return;
    }

    const grab = this.#both;
    const ratio = distance / grab.startSeparation;
    const scale = THREE.MathUtils.clamp(
      grab.startScale * ratio,
      MIN_SCALE,
      MAX_SCALE
    );
    // Recover the effective ratio after clamping so translation stays consistent
    // with the scale actually applied; otherwise the world slides at the limits.
    const appliedRatio = scale / grab.startScale;

    let deltaYaw = yaw - grab.startYaw;
    deltaYaw = Math.atan2(Math.sin(deltaYaw), Math.cos(deltaYaw));
    this.#yawQuat.setFromAxisAngle(this.#up, deltaYaw);

    // Keep whatever point of the world was under the hands' midpoint pinned to
    // the midpoint as it scales and turns. This is what makes it feel held.
    this.#offset
      .subVectors(grab.startPosition, grab.startMidpoint)
      .multiplyScalar(appliedRatio)
      .applyQuaternion(this.#yawQuat);

    this.#target.position.copy(this.#midpoint).add(this.#offset);
    this.#target.quaternion.copy(grab.startQuaternion).premultiply(this.#yawQuat);
    this.#target.scale.setScalar(scale);
    this.#lastScale = scale;
  }

  #updateOneHanded(hand: HandPose): void {
    if (this.#single === null) {
      this.#single = {
        startHand: hand.position.clone(),
        startPosition: this.#target.position.clone()
      };
      return;
    }
    this.#offset.subVectors(hand.position, this.#single.startHand);
    this.#target.position.copy(this.#single.startPosition).add(this.#offset);
  }

  /**
   * Accepts a scale applied from outside the gesture (debug stepping), so the
   * posture readout stays truthful and the next grab starts from reality.
   */
  adoptExternalScale(scale: number): void {
    this.#both = null;
    this.#single = null;
    this.#lastScale = scale;
  }

  /** Snaps the world back to a known pose, for a recentre control. */
  reset(position: THREE.Vector3, scale: number): void {
    this.#both = null;
    this.#single = null;
    this.#referenceScale = scale;
    this.#target.position.copy(position);
    this.#target.quaternion.identity();
    this.#target.scale.setScalar(scale);
    this.#lastScale = scale;
  }
}
